import React, { useState, useEffect } from "react";
import { setLoginScreenVisible, applyPendingReload } from "../utils/pendingReload";
import { login as apiLogin, faceLogin, isPersonalDevice, COMPANY_ID } from "../services/api";
import { useFaceAuth } from "../hooks/useFaceAuth";
import { verifyOfflinePassword, offlineAuthReady, getBundleByUserId } from "../utils/offlineAuth";

/** 会社の設定（admin_face_login）がOFFだと、管理者は顔認証ではログインできない（サーバーが拒否する） */
const ADMIN_FACE_MSG = "管理者（陸上管理者・システム管理者）は顔認証ではログインできません。社員コードとパスワードでログインしてください。";
import PasswordInput from "../components/PasswordInput";
import { CompanyLogo } from "../components/CompanyLogo";
import { InstallAppGuide } from "../components/InstallAppGuide";

interface Props {
  onLogin: (userId: string, token: string, user: Record<string, unknown>, refreshToken?: string) => void;
}

export const LoginPage: React.FC<Props> = ({ onLogin }) => {
  // ログイン中に見つかった新しい版は、ここ（誰もログインしていない画面）で反映する
  useEffect(() => {
    setLoginScreenVisible(true);
    applyPendingReload();
    return () => setLoginScreenVisible(false);
  }, []);
  // 個人スマホは顔データが端末に無いことが多いため、ID/パスワードを既定にする（顔認証へも切替可能）
  // 個人スマホは初回だけ社員コード＋パスワード。顔特徴量が端末に残っていれば顔認証から始める。
  const [mode, setMode] = useState<"face" | "password">(() => {
    if (!isPersonalDevice()) return "face";
    try {
      const stored = JSON.parse(localStorage.getItem(`catms_face_descriptors_${COMPANY_ID}`) ?? '[]') as unknown[];
      return stored.length > 0 ? "face" : "password";
    } catch { return "password"; }
  });
  const [employeeCode, setEmployeeCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const { videoRef, status, message, guide, startCamera, stopCamera, recognizeFace } = useFaceAuth();

  useEffect(() => {
    if (mode === "face") { startCamera(); }
    else { stopCamera(); }
    return () => { stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  /** ID/パスワード入力へ切り替え、分かれば社員コードを入れておく（顔で本人は分かっているので） */
  const switchToPasswordFor = (userId: string, reason: string) => {
    const code = getBundleByUserId(userId)?.employee_code;
    if (code) setEmployeeCode(code);
    setMode("password");
    setError(reason);
  };

  const handleFaceLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const found = await recognizeFace();
      if (!found) {
        setError("顔認証に失敗しました。もう一度試すか、IDとパスワードでログインしてください。");
        return;
      }
      if (!found.faceSecret) {
        setError("この端末では顔認証ログインが未設定です。一度IDとパスワードでログインし、顔認証を再登録してください。");
        return;
      }
      // 端末で本人特定 → サーバから本トークンを取得（オンライン）。
      // オフライン時は端末内情報でローカルログインし、打刻はキュー保存→復帰時に本人トークンで同期。
      try {
        const data = await faceLogin(found.userId, found.faceSecret);
        setSucceeded(true);
        await new Promise(r => setTimeout(r, 600));
        onLogin(
          String((data.user as Record<string, unknown>)["user_id"] ?? found.userId),
          data.access_token,
          data.user,
          data.refresh_token,
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : "";
        const isNetwork = e instanceof TypeError || /fetch|Failed to fetch|NetworkError|ネットワーク|接続|timeout/i.test(msg);
        if (isNetwork) {
          // オフライン: ローカルセッションを確立（トークン空。復帰時にsilent/本人トークンへ昇格）
          setSucceeded(true);
          await new Promise(r => setTimeout(r, 400));
          onLogin(found.userId, "", {
            user_id: found.userId,
            user_name: found.userName,
            user_role: found.userRole ?? "crew",
            company_id: found.companyId ?? "",
            short_id: found.userId,
          });
          return;
        }
        if (msg.includes("管理者")) {
          // 会社の設定で管理者の顔認証が許可されていない。理由を出してID/パスワードへ
          switchToPasswordFor(found.userId, ADMIN_FACE_MSG);
          setSucceeded(false);
          return;
        }
        setError(msg.includes("無効")
          ? "顔認証情報が無効です。IDとパスワードでログインし、顔認証を再登録してください。"
          : "顔認証ログインに失敗しました。IDとパスワードでログインしてください。");
        setSucceeded(false);
      }
    } catch {
      setError("顔認証エラーが発生しました。IDとパスワードでログインしてください。");
      setSucceeded(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employeeCode || !password) {
      setError("社員コードとパスワードを入力してください。");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await apiLogin(employeeCode, password);
      onLogin(String(res.user["user_id"] ?? res.user["id"] ?? ""), res.access_token, res.user, res.refresh_token);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "ログインに失敗しました";
      const isNetwork = err instanceof TypeError || /fetch|Failed to fetch|NetworkError|connect|接続|timeout|net:/i.test(msg);
      if (isNetwork) {
        // オフライン: 端末キャッシュのパスワードハッシュで照合してローカルログイン
        await offlineAuthReady();
        const b = verifyOfflinePassword(employeeCode, password);
        if (b) {
          onLogin(b.user_id, "", {
            user_id: b.user_id, user_name: b.user_name, user_role: b.user_role,
            company_id: b.company_id, employee_code: b.employee_code, short_id: b.user_id,
            avatar_path: b.avatar_path ?? null,
          });
          return;
        }
        setError("オフラインです。この端末で一度オンラインログインした社員コード・パスワードのみ利用できます。");
      } else if (msg.includes("401") || msg.includes("認証") || msg.includes("正しく")) {
        setError("社員コードまたはパスワードが正しくありません。");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // 位置が合ったら自動でログイン処理を開始（ボタン押下不要でスムーズに）。
  // 連打防止のため一度発火したら失敗後2秒はアームしない。
  const autoRef = React.useRef(false);
  useEffect(() => {
    if (mode !== "face") { autoRef.current = false; return; }
    if (guide === "ok" && !loading && !succeeded && !autoRef.current) {
      autoRef.current = true;
      handleFaceLogin().finally(() => { setTimeout(() => { autoRef.current = false; }, 2000); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guide, loading, succeeded, mode]);

  const borderColor = succeeded ? "#22c55e"
    : status === "detecting" || loading ? "#0ea5e9"
    : guide === "ok" ? "#22c55e"
    : guide === "searching" || guide === "idle" ? "rgba(255,255,255,0.45)"
    : "#f59e0b"; // far/near/off_center = オレンジ
  const canLogin = !loading && !succeeded && (guide === "ok" || status === "error");

  return (
    <div style={{
      // 100vh はキーボードやシステムバーの分を含んでしまうため、実際に見えている高さ（dvh）で組む。
      // 船内端末は表示サイズを大きくしていることが多く、CSS上の画面が 320x693px 程度しかない。
      // ログインでスクロールさせないよう、この高さに収まるレイアウトにする。
      minHeight: "100dvh",
      background: "linear-gradient(160deg, #0a2342 0%, #1a5276 35%, #1f8a9e 65%, #2ec4b6 100%)",
      display: "flex", flexDirection: "column", alignItems: "center",
      fontFamily: "Inter, sans-serif",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"],
    }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse-green { 0%,100% { box-shadow: 0 0 16px 4px rgba(34,197,94,0.4); } 50% { box-shadow: 0 0 32px 10px rgba(34,197,94,0.8); } }
        @keyframes detecting-dash { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -200; } }
      `}</style>

      {/* ヘッダー */}
      {/* app-header クラスがセーフエリア分の上余白（ステータスバー＋カメラ穴）を既に入れる。
          ここで marginTop を重ねると上に空白が二重に入り、その分だけ全体が下へ押し出されて
          画面下端のボタンが切れていた（arrows FCG02 で確認）。余白は app-header に一本化する。 */}
      <div className="app-header" style={{ textAlign: "center", zIndex: 1 }}>
        <CompanyLogo height={52} dark />
        <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, marginTop: 10, letterSpacing: "2px" }}>
          船員勤怠管理システム
        </p>
      </div>

      {/* メインコンテンツ */}
      <div style={{
        width: "100%", maxWidth: 380, padding: "16px 28px", zIndex: 1, boxSizing: "border-box",
        // 低い画面では著作権表示を出さない（.login-footer）ので、ここが最下段になる。
        // ナビゲーションバーに操作ボタンが潜らないよう、下セーフエリア分を確保する。
        paddingBottom: "calc(16px + max(var(--app-safe-bottom, 0px), env(safe-area-inset-bottom, 0px)) / var(--zoom, 1))",
      }}>
        {/* 個人スマホをブラウザで開いたときだけ「ホーム画面に追加」の手順を出す */}
        <InstallAppGuide dark />

        {/* ── 顔認証モード ── */}
        {mode === "face" && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, marginBottom: 10, textAlign: "center" }}>
              カメラに顔を向けてログインしてください
            </p>

            {/* エラーメッセージ */}
            {error && !succeeded && (
              <div style={{
                width: "100%", background: "rgba(239,68,68,0.15)",
                border: "1px solid rgba(239,68,68,0.35)", borderRadius: 12,
                padding: "12px 16px", color: "#fecaca", fontSize: 13,
                marginBottom: 16, boxSizing: "border-box",
              }}>
                ⚠ {error}
              </div>
            )}

            {/* カメラ + 楕円ガイド */}
            {/* 高さを画面に合わせて縮める。固定 220x260 だと表示サイズを大きくした端末で
                画面に収まらず、ログインのたびにスクロールが必要になっていた。
                縦横比は保ち、小さい画面でも顔が収まる下限（150px）は割らない。 */}
            <div style={{
              position: "relative", height: "min(260px, 28dvh)", aspectRatio: "220 / 260",
              minHeight: 150, marginBottom: 12,
            }}>
              <div style={{
                width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden",
                border: `3px solid ${borderColor}`,
                boxShadow: succeeded
                  ? "0 0 24px 6px rgba(34,197,94,0.6)"
                  : status === "detecting"
                  ? "0 0 16px 4px rgba(14,165,233,0.5)"
                  : undefined,
                transition: "border-color 0.4s, box-shadow 0.4s",
                animation: succeeded ? "pulse-green 1.5s ease-in-out infinite" : undefined,
              }}>
                <video
                  ref={videoRef} autoPlay muted playsInline
                  style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }}
                />

                {/* ローディングオーバーレイ */}
                {status === "loading" && (
                  <div style={{
                    position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)",
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", gap: 8,
                  }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                      style={{ animation: "spin 1s linear infinite" }}>
                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    <span style={{ color: "white", fontSize: 12 }}>起動中...</span>
                  </div>
                )}

                {/* 成功オーバーレイ */}
                {succeeded && (
                  <div style={{
                    position: "absolute", inset: 0,
                    background: "rgba(34,197,94,0.3)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width="70" height="70" viewBox="0 0 70 70" fill="none">
                      <circle cx="35" cy="35" r="33" fill="rgba(34,197,94,0.4)" stroke="#22c55e" strokeWidth="3" />
                      <path d="M19 35l11 11 21-21" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
              </div>

              {/* 顔の位置ガイド楕円 */}
              {(status === "ready" || status === "detecting") && !succeeded && (
                <svg viewBox="0 0 220 260" preserveAspectRatio="none"
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
                  <ellipse cx="110" cy="130" rx="65" ry="82"
                    fill="none"
                    stroke={status === "detecting" ? "rgba(14,165,233,0.7)" : "rgba(255,255,255,0.2)"}
                    strokeWidth="1.5"
                    strokeDasharray={status === "detecting" ? "10 5" : "6 6"}
                    style={{
                      animation: status === "detecting"
                        ? "detecting-dash 1.5s linear infinite" : undefined,
                    }}
                  />
                </svg>
              )}
            </div>

            {/* ステータステキスト */}
            <div style={{
              fontSize: succeeded ? 16 : 12,
              fontWeight: succeeded ? 700 : 400,
              color: succeeded ? "#86efac"
                : status === "error" ? "#fca5a5"
                : status === "detecting" ? "#7dd3fc"
                : "rgba(255,255,255,0.6)",
              marginBottom: 10, textAlign: "center",
              transition: "all 0.3s",
            }}>
              {succeeded
                ? `✓ 認証成功 — ログイン中...`
                : (message || "カメラの前に顔を合わせてください")}
            </div>

            {/* 顔認証ボタン */}
            <button
              onClick={handleFaceLogin}
              disabled={!canLogin || succeeded}
              style={{
                width: "100%", padding: "16px", borderRadius: 16, border: "none",
                background: canLogin && !succeeded
                  ? "linear-gradient(135deg, #0ea5e9, #2ec4b6)"
                  : "rgba(255,255,255,0.18)",
                color: "white", fontSize: 15, fontWeight: 700,
                cursor: canLogin && !succeeded ? "pointer" : "not-allowed",
                marginBottom: 12,
                boxShadow: canLogin && !succeeded ? "0 4px 20px rgba(14,165,233,0.4)" : "none",
                transition: "all 0.3s",
              }}
            >
              {loading ? "認証中..." : succeeded ? "ログイン中..." : guide === "ok" ? "顔認証でログイン" : "顔を枠に合わせてください"}
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, width: "100%" }}>
              <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.2)" }} />
              <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 12 }}>または</span>
              <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.2)" }} />
            </div>

            <button
              onClick={() => setMode("password")}
              style={{
                width: "100%", padding: "16px", borderRadius: 16,
                border: "1.5px solid rgba(255,255,255,0.3)",
                background: "rgba(255,255,255,0.12)", color: "white",
                fontSize: 15, fontWeight: 600, cursor: "pointer",
              }}
            >
              社員コード / パスワードでログイン
            </button>
          </div>
        )}

        {/* ── パスワードモード ── */}
        {mode === "password" && (
          <div style={{ width: "100%" }}>
            <div style={{
              background: "rgba(255,255,255,0.12)", backdropFilter: "blur(24px)",
              borderRadius: 24, border: "1px solid rgba(255,255,255,0.2)",
              padding: "36px 24px",
            }}>
              <h2 style={{ color: "white", fontSize: 20, fontWeight: 700, marginBottom: 28, textAlign: "center" }}>
                ログイン
              </h2>
              {error && (
                <div style={{
                  background: "rgba(239,68,68,0.15)", borderRadius: 12,
                  padding: "12px 16px", color: "#fecaca", fontSize: 13, marginBottom: 20,
                }}>
                  ⚠ {error}
                </div>
              )}
              <form onSubmit={handlePasswordLogin}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{
                    display: "block", color: "rgba(255,255,255,0.6)",
                    fontSize: 11, fontWeight: 600, marginBottom: 8, textTransform: "uppercase",
                  }}>社員コード</label>
                  <input
                    type="text" value={employeeCode}
                    onChange={e => setEmployeeCode(e.target.value)}
                    placeholder="例: crew01"
                    autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    autoComplete="username" inputMode="text"
                    style={{
                      width: "100%", padding: "13px 16px", borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.2)",
                      background: "rgba(255,255,255,0.1)", color: "white",
                      fontSize: 15, outline: "none", boxSizing: "border-box",
                    }}
                  />
                </div>
                <div style={{ marginBottom: 28 }}>
                  <label style={{
                    display: "block", color: "rgba(255,255,255,0.6)",
                    fontSize: 11, fontWeight: 600, marginBottom: 8, textTransform: "uppercase",
                  }}>パスワード</label>
                  <PasswordInput
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="パスワードを入力"
                    style={{
                      width: "100%", padding: "13px 16px", borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.2)",
                      background: "rgba(255,255,255,0.1)", color: "white",
                      fontSize: 15, outline: "none", boxSizing: "border-box",
                    }}
                  />
                </div>
                <button
                  type="submit" disabled={loading}
                  style={{
                    width: "100%", padding: "15px", borderRadius: 14, border: "none",
                    background: loading
                      ? "rgba(255,255,255,0.2)"
                      : "linear-gradient(135deg, #0ea5e9, #2ec4b6)",
                    color: "white", fontSize: 16, fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "ログイン中..." : "ログイン"}
                </button>
              </form>
            </div>
            <button
              onClick={() => { setError(""); setMode("face"); }}
              style={{
                marginTop: 16, width: "100%", padding: "13px", borderRadius: 12,
                border: "none", background: "transparent",
                color: "rgba(255,255,255,0.45)", fontSize: 14, cursor: "pointer",
              }}
            >
              ← 顔認証に戻る
            </button>
          </div>
        )}
      </div>

      {/* フッター。
          下余白を 40px 決め打ちにしていたため、3ボタンのナビゲーションバー（約48px）がある端末では
          著作権表示がバーの下に潜り込んでいた（arrows FCG02 で確認）。
          端末が報告する下のセーフエリアを足し、報告しない端末でもバーを越える高さを確保する。
          #root には文字サイズの zoom がかかるので、実寸に戻すため倍率で割る。 */}
      <div className="login-footer" style={{
        marginTop: "auto",
        paddingBottom: "calc(8px + max(var(--app-safe-bottom, 0px), env(safe-area-inset-bottom, 0px)) / var(--zoom, 1))",
        textAlign: "center", zIndex: 1,
      }}>
        <p style={{ color: "rgba(255,255,255,0.2)", fontSize: 11 }}>© 2026 UNISON TECHNOLOGY</p>
      </div>
    </div>
  );
};

export default LoginPage;
